// admin/src/pages/GatewayMonitoring/index.js
'use strict';

import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Tabs,
  Tab,
  Stack,
  Button,
  Paper,
  Alert,
  Grid,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import RouterIcon from '@mui/icons-material/Router';
import HistoryIcon from '@mui/icons-material/History';
import SecurityIcon from '@mui/icons-material/Security';

import ReusableHeader from '../../components/Header/ReusableHeader';
import { ColorModeContext } from '../../index';

import {
  GATEWAY_PERMISSIONS,
  hasGatewayPermission,
} from './gatewayPermissions';
import {
  listGateways,
  extractGatewayErrorMessage,
} from './gatewayApi';

import UnhealthySummaryBanner from './components/UnhealthySummaryBanner';
import DirectorySyncCard from './components/DirectorySyncCard';
import LoginRelayCard from './components/LoginRelayCard';
import GatewayHealthCards from './components/GatewayHealthCards';
import GatewayEventsTable from './components/GatewayEventsTable';
import EnrollmentModal from './components/EnrollmentModal';
import GatewaySettingsDialog from './components/GatewaySettingsDialog';
import RenameGatewayDialog from './components/RenameGatewayDialog';
import DeleteConfirmDialog from './components/DeleteConfirmDialog';

/* =========================================================
   mEDGE DESIGN TOKENS
========================================================= */
const LIGHT_VARS = {
  '--vault-navy': '#0B2545',
  '--edge-blue': '#1F6FEB',
  '--canvas': '#FFFFFF',
  '--mist': '#F7F9FC',
  '--hairline': '#E4E9F0',
  '--blue-tint': '#EAF2FE',
  '--slate': '#6B7A90',
  '--secure': '#00A88F',
  '--high': '#E2622B',
  '--elevated': '#F0A202',
  '--critical': '#D6323A',
  '--border-radius': '6px',
};

const DARK_VARS = {
  '--vault-navy': '#F4F7FB',
  '--edge-blue': '#4C9AFF',
  '--canvas': '#0F1729',
  '--mist': '#252b3b',
  '--hairline': 'rgba(255,255,255,0.14)',
  '--blue-tint': 'rgba(76,154,255,0.16)',
  '--slate': '#93A4BD',
  '--secure': '#2FD3B8',
  '--high': '#F07C46',
  '--elevated': '#FFC24B',
  '--critical': '#FF6B72',
  '--border-radius': '6px',
};

export default function GatewayMonitoringPage() {
  const { mode } = useContext(ColorModeContext || { mode: 'light' });
  const V = mode === 'dark' ? DARK_VARS : LIGHT_VARS;

  // Stored user principal
  const currentUser = useMemo(() => {
    try {
      const raw = typeof window !== 'undefined' ? (localStorage.getItem('user') || sessionStorage.getItem('user')) : null;
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  // Strict permission gating: decided ONLY by owner check + exact permission strings
  const canRead = hasGatewayPermission(currentUser, GATEWAY_PERMISSIONS.READ);
  const canCreate = hasGatewayPermission(currentUser, GATEWAY_PERMISSIONS.CREATE);
  const canUpdate = hasGatewayPermission(currentUser, GATEWAY_PERMISSIONS.UPDATE);
  const canDelete = hasGatewayPermission(currentUser, GATEWAY_PERMISSIONS.DELETE);

  // Tab State: 0 = Gateway Health, 1 = Log & Audit Events
  const [activeTab, setActiveTab] = useState(0);

  // Gateway data state
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  // Modal dialog states
  const [enrollmentOpen, setEnrollmentOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteActionType, setDeleteActionType] = useState('revoke'); // 'revoke' | 'delete'
  const [selectedGateway, setSelectedGateway] = useState(null);

  // Interval timer ref for auto-refresh
  const refreshTimerRef = useRef(null);

  /* =====================================================
     LOAD GATEWAYS
  ===================================================== */
  const loadGateways = useCallback(async () => {
    if (!canRead) return;
    setLoading(true);
    setError(null);
    try {
      const res = await listGateways();
      const items = Array.isArray(res?.data) ? res.data : [];
      setGateways(items);
      setLastRefreshedAt(new Date());
    } catch (err) {
      setError(extractGatewayErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [canRead]);

  // Initial fetch and visibility-aware 60s auto-refresh
  useEffect(() => {
    if (!canRead) return;

    loadGateways();

    const startTimer = () => {
      stopTimer();
      refreshTimerRef.current = setInterval(() => {
        loadGateways();
      }, 60000);
    };

    const stopTimer = () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        stopTimer();
      } else if (document.visibilityState === 'visible') {
        loadGateways();
        startTimer();
      }
    };

    startTimer();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopTimer();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [canRead, loadGateways]);

  /* =====================================================
     ACCESS DENIED STATE
     Matches SCIM page parity exactly: 0 requests sent
  ===================================================== */
  if (!canRead) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }} data-testid="gateway-access-denied-container">
        <ReusableHeader title="Gateway Monitoring" />
        <Paper
          variant="outlined"
          sx={{
            p: 6,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 340,
            mt: 3,
          }}
          data-testid="gateway-access-denied"
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <SecurityIcon color="error" sx={{ fontSize: 32 }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Access Denied
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460 }}>
            You do not have permission to view Gateway Monitoring. Please contact your organization administrator to
            request access.
          </Typography>
        </Paper>
      </Container>
    );
  }

  /* =====================================================
     RENDER AUTHORIZED PAGE
  ===================================================== */
  return (
    <Box p={2} style={V} sx={{ minHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="xl" sx={{ py: 2 }}>
        {/* Page Header */}
        <ReusableHeader
          title="Gateway Monitoring"
          breadcrumbs={[{ label: 'Gateway Monitoring' }]}
        />

        {/* Action Toolbar */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mt: 2,
            mb: 2.5,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, newVal) => setActiveTab(newVal)}
            aria-label="gateway monitoring tabs"
            sx={{
              minHeight: 40,
              '& .MuiTab-root': {
                minHeight: 40,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
                px: 2,
              },
            }}
          >
            <Tab
              icon={<RouterIcon fontSize="small" sx={{ mr: 0.5 }} />}
              iconPosition="start"
              label="Gateway Health"
              data-testid="tab-gateway-health"
            />
            <Tab
              icon={<HistoryIcon fontSize="small" sx={{ mr: 0.5 }} />}
              iconPosition="start"
              label="Log & Audit Events"
              data-testid="tab-gateway-events"
            />
          </Tabs>

          <Stack direction="row" spacing={1.5} alignItems="center">
            {lastRefreshedAt && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: { xs: 'none', sm: 'inline-block' }, mr: 0.5 }}
                data-testid="last-refreshed-text"
              >
                Updated {lastRefreshedAt.toLocaleTimeString()}
              </Typography>
            )}

            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => loadGateways()}
              disabled={loading}
              data-testid="refresh-gateways-btn"
            >
              Refresh
            </Button>

            {canUpdate && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => setSettingsOpen(true)}
                data-testid="open-settings-btn"
              >
                Settings
              </Button>
            )}

            {canCreate && (
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setEnrollmentOpen(true)}
                data-testid="open-enrollment-btn"
              >
                Add Gateway
              </Button>
            )}
          </Stack>
        </Box>

        {/* Global Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Tab 0: Gateway Health */}
        {activeTab === 0 && (
          <Box>
            <UnhealthySummaryBanner gateways={gateways} mode={mode} />
            <Grid container spacing={2.5}>
              <Grid item xs={12} lg={6}>
                <DirectorySyncCard currentUser={currentUser} mode={mode} onRunStarted={() => loadGateways()} />
              </Grid>
              <Grid item xs={12} lg={6}>
                <LoginRelayCard currentUser={currentUser} mode={mode} onRefresh={() => loadGateways()} />
              </Grid>
            </Grid>
            <GatewayHealthCards
              gateways={gateways}
              loading={loading}
              mode={mode}
              canCreate={canCreate}
              canUpdate={canUpdate}
              canDelete={canDelete}
              onAddGateway={() => setEnrollmentOpen(true)}
              onRenameGateway={(gw) => {
                setSelectedGateway(gw);
                setRenameOpen(true);
              }}
              onRevokeGateway={(gw) => {
                setSelectedGateway(gw);
                setDeleteActionType('revoke');
                setDeleteDialogOpen(true);
              }}
              onDeleteGateway={(gw) => {
                setSelectedGateway(gw);
                setDeleteActionType('delete');
                setDeleteDialogOpen(true);
              }}
            />
          </Box>
        )}

        {/* Tab 1: Log & Audit Events */}
        {activeTab === 1 && (
          <Box>
            <GatewayEventsTable
              gateways={gateways}
              mode={mode}
              canRead={canRead}
            />
          </Box>
        )}

        {/* Dialog: Enrollment Modal */}
        <EnrollmentModal
          open={enrollmentOpen}
          onClose={() => setEnrollmentOpen(false)}
          mode={mode}
          canDelete={canDelete}
        />

        {/* Dialog: Gateway Settings */}
        <GatewaySettingsDialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          onSettingsUpdated={loadGateways}
          mode={mode}
          canUpdate={canUpdate}
        />

        {/* Dialog: Rename Gateway */}
        <RenameGatewayDialog
          open={renameOpen}
          gateway={selectedGateway}
          onClose={() => {
            setRenameOpen(false);
            setSelectedGateway(null);
          }}
          onSuccess={() => {
            setRenameOpen(false);
            setSelectedGateway(null);
            loadGateways();
          }}
        />

        {/* Dialog: Revoke or Delete Gateway */}
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          gateway={selectedGateway}
          actionType={deleteActionType}
          onClose={() => {
            setDeleteDialogOpen(false);
            setSelectedGateway(null);
          }}
          onSuccess={() => {
            setDeleteDialogOpen(false);
            setSelectedGateway(null);
            loadGateways();
          }}
        />
      </Container>
    </Box>
  );
}
